import { Component, computed, input, output } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import { ModalContentComponent } from '../../modal-content/modal-content.component';
import { ActionButton } from '../../modal-content/action-button';
import { toFirstLetterLower } from '../../../helper/stringhelper';
import { filterTrue } from '../../../pipe/rxjs/operators';
import { ModalService } from '../../../service/modal.service';
import { AccountingService } from '../../../store/accounting/accounting.service';
import {
  AccountMoveModel,
  AccountMoveOwnerContext,
  AccountMoveStatus,
  AccountMoveTabOptions,
  BindType,
  MoveType,
  SALES_PERSON_COMMISSION_ID,
} from '../../../store/accounting/types';
import { ButtonType } from '../../../store/subheader/type';
import { SourceEnum } from '../../../store/typeahead/types';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

/**
 * Onglet des quittances d'une entité porteuse (contrat ou sinistre).
 *
 * Le composant ne connaît aucun service métier : l'appelant lui passe les écritures et un
 * {@link AccountMoveOwnerContext}, et se charge de recharger son agrégat quand `refreshRequested` est
 * émis. C'est ce qui lui permet de servir les deux écrans sans que l'un écrase le cache de l'autre.
 */
@Component({
  selector: 'app-account-moves-tab',
  templateUrl: './account-moves-tab.component.html',
  styleUrls: ['./account-moves-tab.component.scss'],
  standalone: false,
})
export class AccountMovesTabComponent {
  readonly accountMoves = input.required<AccountMoveModel[]>();
  readonly context = input.required<AccountMoveOwnerContext>();
  readonly isClient = input<boolean>(true);
  readonly isReadOnly = input<boolean>(false);
  readonly options = input<AccountMoveTabOptions>({});

  /** Émis après toute action ayant modifié les écritures : l'appelant recharge son agrégat. */
  readonly refreshRequested = output<void>();

  /** Options de l'appelant, complétées par les défauts qui reproduisent l'affichage historique. */
  readonly effectiveOptions = computed<Required<AccountMoveTabOptions>>(() => {
    const isClient = this.isClient();
    const options = this.options();

    return {
      showAddInvoice: options.showAddInvoice ?? isClient,
      showBindPayment: options.showBindPayment ?? isClient,
      showBindRepayment: options.showBindRepayment ?? true,
      showSlipColumn: options.showSlipColumn ?? !isClient,
      showPeriodColumn: options.showPeriodColumn ?? true,
    };
  });

  protected readonly ButtonType = ButtonType;
  protected readonly SALES_PERSON_COMMISSION_ID = SALES_PERSON_COMMISSION_ID;

  constructor(
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
    private readonly accountingService: AccountingService,
  ) {}

  openModalCreateInvoiceExtended() {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'xl',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAddInvoiceExtended,
      );
    modalRef.componentInstance.title = 'INVOICE_EXTENDED_LBL_ACCOUNTMOVE_ADD';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: 'invoiceExtendedCreate',
      },
    ];
    modalRef.componentInstance.inputParams = {
      ...this.ownerInputParams(),
      source: SourceEnum.Modal,
      isClient: this.isClient(),
      contractInsuranceCompanyContactId:
        this.context().insuranceCompanyContactId,
    };

    this.onModalClosed(modalRef);
  }

  openModalBindAccountMoves(moveType?: MoveType, accountMoveId?: number) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'xl',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAssign,
      );
    modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_ASSIGN_INVOICE';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('accountMoveAssign') + 'Create',
      },
    ];

    let bindType: BindType;
    if (moveType == 'Entry') {
      if (this.isClient()) {
        bindType = 'Payment';
      } else {
        bindType = 'Slip';
      }
    } else {
      bindType = 'Repayment';
    }

    modalRef.componentInstance.inputParams = {
      ...this.ownerInputParams(),
      invoiceId: accountMoveId,
      source: SourceEnum.Modal,
      bindType: bindType,
    };

    this.onModalClosed(modalRef);
  }

  openModalRepaymentBindAccountMoves(accountMoveId?: number) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAssignRepayment,
      );
    modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_ASSIGN_REPAYMENT';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('accountMoveRepaymentAssign') + 'Create',
      },
    ];
    modalRef.componentInstance.inputParams = {
      ...this.ownerInputParams(),
      source: SourceEnum.Modal,
    };

    this.onModalClosed(modalRef);
  }

  openModalBindAccountMovesDetails(
    invoice: AccountMoveModel,
    type: BindType,
    isReadOnly: boolean,
    isAdminOnly: boolean,
  ) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: true,
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAssignDetails,
      );
    modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_PAYMENTS_INVOICES';
    if (type === 'Repayment') {
      modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_PAYMENTS_REPAYMENT';
    }

    modalRef.componentInstance.closeCrossButton = true;
    modalRef.componentInstance.cancelButton = false;
    let buttons: ActionButton[] = [];

    if (isAdminOnly) {
      buttons.push(<ActionButton>{
        label: 'PAYMENT_LBL_UNBIND',
        actionName: 'accountMovesUnbind',
      });
    }
    if (type === 'Payment') {
      buttons.push(<ActionButton>{
        label: 'ACCOUNTMOVE_LBL_GENERATE_PDF',
        actionName: 'accountMovesPDF',
      });
    }

    modalRef.componentInstance.buttons = buttons;

    switch (type) {
      case 'Repayment':
        modalRef.componentInstance.inputParams = {
          source: SourceEnum.Modal,
          accountMoveId: invoice.id,
          labelInvoices: 'ACCOUNTMOVE_LBL_CLIENT',
          labelPayments: 'ACCOUNTMOVE_LBL_VENDOR',
          isVendorInvoice: true,
          bindType: 'Repayment',
        };
        break;
      case 'Slip':
        modalRef.componentInstance.inputParams = {
          source: SourceEnum.Modal,
          accountMoveId: invoice.id,
          bindType: 'Slip',
        };
        break;
      case 'Payment':
      default:
        modalRef.componentInstance.inputParams = {
          source: SourceEnum.Modal,
          accountMoveId: invoice.id,
          bindType: 'Payment',
        };
        break;
    }

    this.onModalClosed(modalRef);
  }

  onClickChangeAccountMoveStatus(id: number, status: AccountMoveStatus) {
    let result$ = this.accountingService.changeAccountMoveStatus.call({
      id: id.toString(),
      status: status,
    });

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      this.refreshRequested.emit();
      this.accountingService.changeAccountMoveStatus.reset();
    });
  }

  onClickDeleteAccountMove(id: number) {
    let result$ = this.accountingService.delete.call(id);

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      this.refreshRequested.emit();
      this.accountingService.delete.reset();
    });
  }

  onClickEditAccountMove(id: number) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAdd,
      );
    modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_EDIT';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('AccountMove') + 'Edit',
      },
    ];
    modalRef.componentInstance.inputParams = {
      ...this.ownerInputParams(),
      accountMoveId: id,
      source: SourceEnum.Modal,
      isClient: this.isClient(),
      contractInsuranceCompanyContactId:
        this.context().insuranceCompanyContactId,
    };

    this.onModalClosed(modalRef);
  }

  openModalBindSlipDetails(
    accountMove: AccountMoveModel,
    journalId: number,
    journalName: string,
  ) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: true,
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.AccountMoveAssignSlipDetails,
      );
    modalRef.componentInstance.title = 'ACCOUNTMOVE_LBL_INVOICE_JOURNAL';

    modalRef.componentInstance.closeCrossButton = true;
    modalRef.componentInstance.cancelButton = false;
    modalRef.componentInstance.buttons = [];

    modalRef.componentInstance.inputParams = {
      source: SourceEnum.Modal,
      invoice: accountMove,
      journalId: journalId,
      journalName: journalName,
    };

    //modale de consultation : rien à recharger à la fermeture
    modalRef.result.catch(() => {
      //avoid error on cancel
    });
  }

  openModalCreateAndBindPayment(
    accountMoveId: number,
    contactIsCompany: boolean,
    amount: number,
    creditAccountId: number,
  ) {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(ModalComponentKey.PaymentAdd);

    modalRef.componentInstance.title = 'PAYMENT_LBL_ADD';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('Payment') + 'CreateAndBind',
      },
    ];
    modalRef.componentInstance.inputParams = {
      contactId: this.context().contactId,
      contactIsCompany: contactIsCompany,
      isReimbursement: contactIsCompany,
      creditAccountId: creditAccountId,
      amount: amount,
      invoiceAccountMoveId: accountMoveId,
      source: SourceEnum.Modal,
    };

    this.onModalClosed(modalRef);
  }

  triggerAction(actionName: string, accountMoveId: number) {
    let status: AccountMoveStatus = 'Draft';
    switch (actionName) {
      case 'CancelAccountMove':
        status = 'Cancel';
        this.onClickChangeAccountMoveStatus(accountMoveId, status);
        break;
      case 'ValidateAccountMove':
        status = 'Posted';
        this.onClickChangeAccountMoveStatus(accountMoveId, status);
        break;
      case 'DeleteAccountMove':
        this.onClickDeleteAccountMove(accountMoveId);
        break;
      case 'EditAccountMove':
        this.onClickEditAccountMove(accountMoveId);
        break;
    }
  }

  /**
   * Ancre poussée dans les `inputParams` des modales. `insuranceClaimId` est toujours présent comme clé :
   * `ngComponentOutlet` fait un `setInput()` par clé, chaque composant de contenu doit donc la déclarer.
   */
  private ownerInputParams(): {
    contractId: number;
    insuranceClaimId?: number;
  } {
    const context = this.context();

    return {
      contractId: context.contractId,
      insuranceClaimId: context.insuranceClaimId,
    };
  }

  private onModalClosed(modalRef: NgbModalRef): void {
    modalRef.result
      .then((id) => {
        if (id >= 0) {
          this.refreshRequested.emit();
        }
      })
      .catch(() => {
        //un dismiss rejette la promesse : rien à recharger, et sans catch la rejection reste non gérée
      });
  }
}
